import React, { useEffect, useState } from "react";
import {
  Landmark,
  Plus,
  Trash2,
  Edit3,
  Copy,
  Check,
  Building2,
  CreditCard,
  X,
  Save,
  Eye,
} from "lucide-react";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import {
  BankAccountItem,
  getBankAccounts,
  createBankAccount,
  updateBankAccount,
  deleteBankAccount,
} from "../services/admin";

export const BankAccountSetting: React.FC = () => {
  const [accounts, setAccounts] = useState<BankAccountItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<string | number | null>(null);
  const [formData, setFormData] = useState<{
    account_name: string;
    account_number: string;
    bank_name: string;
  }>({
    account_name: "",
    account_number: "",
    bank_name: "",
  });

  // View Details Modal State
  const [viewAccount, setViewAccount] = useState<BankAccountItem | null>(null);

  // Copied Account Number indicator
  const [copiedId, setCopiedId] = useState<string | number | null>(null);

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const res = await getBankAccounts();
      if (res?.data && Array.isArray(res.data)) {
        setAccounts(res.data);
      } else if (Array.isArray(res)) {
        setAccounts(res);
      } else {
        setAccounts([]);
      }
    } catch (error) {
      console.error("Failed to fetch bank accounts:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const openAddModal = () => {
    setEditingId(null);
    setFormData({
      account_name: "",
      account_number: "",
      bank_name: "",
    });
    setIsModalOpen(true);
  };

  const openEditModal = (account: BankAccountItem) => {
    if (!account.id) return;
    setEditingId(account.id);
    setFormData({
      account_name: account.account_name || "",
      account_number: account.account_number || "",
      bank_name: account.bank_name || "",
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
    setFormData({
      account_name: "",
      account_number: "",
      bank_name: "",
    });
  };

  const handleCopy = (accountNumber: string, id?: string | number) => {
    navigator.clipboard.writeText(accountNumber);
    if (id !== undefined) setCopiedId(id);
    toast.success("Account number copied to clipboard!");
    setTimeout(() => {
      setCopiedId(null);
    }, 2000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.account_name.trim()) {
      toast.error("Please enter account name");
      return;
    }
    if (!formData.account_number.trim()) {
      toast.error("Please enter account number");
      return;
    }
    if (!formData.bank_name.trim()) {
      toast.error("Please enter bank name");
      return;
    }

    setSubmitting(true);
    try {
      if (editingId) {
        await updateBankAccount({
          accounts: {
            id: editingId,
            account_name: formData.account_name.trim(),
            account_number: formData.account_number.trim(),
            bank_name: formData.bank_name.trim(),
          },
        });
      } else {
        await createBankAccount({
          accounts: [
            {
              account_name: formData.account_name.trim(),
              account_number: formData.account_number.trim(),
              bank_name: formData.bank_name.trim(),
            },
          ],
        });
      }

      closeModal();
      fetchAccounts();
    } catch (error) {
      console.error("Error saving bank account:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (account: BankAccountItem) => {
    if (!account.id) return;

    Swal.fire({
      title: "Delete Bank Account?",
      text: `Are you sure you want to delete "${account.account_name}" (${account.bank_name})?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, delete it",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteBankAccount({ id: account.id! });
          if (viewAccount?.id === account.id) {
            setViewAccount(null);
          }
          fetchAccounts();
        } catch (error) {
          console.error("Error deleting bank account:", error);
        }
      }
    });
  };

  const labelStyle =
    "text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block ml-1";
  const inputContainer =
    "relative flex items-center bg-[#F8FAFC] rounded-[1.5rem] border border-slate-100 focus-within:border-blue-400 focus-within:bg-white transition-all px-5 py-3.5";
  const inputStyle =
    "w-full bg-transparent outline-none text-slate-700 font-bold text-sm ml-3 placeholder:text-slate-300";

  return (
    <div className="animate-in fade-in duration-500">
      {/* Header Info */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 pb-6 border-b border-slate-100 gap-4">
        <div>
          <h2 className="text-[36px] font-black text-[#1E293B] leading-tight mb-1">
            Bank Accounts
          </h2>
          <p className="text-slate-400 font-bold text-sm">
            Manage school settlement and fee payment bank accounts.
          </p>
        </div>

        <button
          type="button"
          onClick={openAddModal}
          className="bg-blue-600 hover:bg-blue-700 text-white px-7 py-4 rounded-[1.5rem] font-black text-xs uppercase tracking-widest transition-all shadow-lg shadow-blue-100 flex items-center gap-2"
        >
          <Plus size={18} />
          Add Account
        </button>
      </div>

      {/* Content Area */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-slate-400 font-bold text-sm">Loading bank accounts...</p>
        </div>
      ) : accounts.length === 0 ? (
        <div className="bg-[#F8FAFC] rounded-[2.5rem] p-12 text-center border-2 border-dashed border-slate-200 flex flex-col items-center justify-center">
          <div className="w-20 h-20 bg-blue-50 text-blue-600 rounded-3xl flex items-center justify-center mb-5 shadow-sm">
            <Landmark size={36} />
          </div>
          <h3 className="text-xl font-black text-slate-800 mb-2">No Bank Accounts Yet</h3>
          <p className="text-slate-400 font-medium text-sm max-w-sm mb-6">
            Add bank account details so parents and administrators can easily reference school accounts for payment.
          </p>
          <button
            type="button"
            onClick={openAddModal}
            className="bg-[#0F172A] hover:bg-slate-800 text-white px-8 py-4 rounded-[1.5rem] font-black text-xs uppercase tracking-widest transition-all shadow-xl flex items-center gap-2"
          >
            <Plus size={16} />
            Add First Bank Account
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-6 w-full">
          {accounts.map((account) => (
            <div
              key={account.id || account.account_number}
              className="bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 text-white rounded-[2.2rem] p-6 shadow-xl hover:shadow-2xl transition-all relative overflow-hidden group border border-slate-700/50 flex flex-col justify-between min-h-[220px] w-full"
            >
              {/* Background decorative shine */}
              <div className="absolute -right-10 -bottom-10 w-44 h-44 bg-blue-500/10 rounded-full blur-2xl group-hover:bg-blue-500/20 transition-all pointer-events-none" />

              <div>
                {/* Top Row: Bank Icon, Bank Name & Action Buttons */}
                <div className="flex justify-between items-start mb-6 gap-2">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-11 h-11 bg-white/10 backdrop-blur-md rounded-2xl flex items-center justify-center text-blue-400 border border-white/10 shrink-0">
                      <Landmark size={20} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="text-[9px] font-black text-blue-300 uppercase tracking-widest block">
                        Bank Name
                      </span>
                      <h4 className="text-base sm:text-lg font-black text-white leading-tight truncate">
                        {account.bank_name}
                      </h4>
                    </div>
                  </div>

                  {/* Actions Pill */}
                  <div className="flex items-center gap-1 bg-white/10 backdrop-blur-md rounded-2xl p-1 border border-white/10 shrink-0">
                    <button
                      type="button"
                      onClick={() => setViewAccount(account)}
                      className="p-2 text-blue-300 hover:text-white hover:bg-white/10 rounded-xl transition-all"
                      title="View Details"
                    >
                      <Eye size={15} />
                    </button>
                    <button
                      type="button"
                      onClick={() => openEditModal(account)}
                      className="p-2 text-slate-300 hover:text-white hover:bg-white/10 rounded-xl transition-all"
                      title="Edit Account"
                    >
                      <Edit3 size={15} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(account)}
                      className="p-2 text-rose-400 hover:text-rose-300 hover:bg-rose-500/20 rounded-xl transition-all"
                      title="Delete Account"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>

                {/* Account Number Display */}
                <div className="bg-white/5 rounded-2xl p-3.5 border border-white/10 mb-5 flex items-center justify-between gap-2 min-w-0">
                  <div className="min-w-0 flex-1">
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-0.5">
                      Account Number
                    </span>
                    <span className="font-mono text-xl sm:text-2xl font-black tracking-widest text-white truncate block">
                      {account.account_number}
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleCopy(account.account_number, account.id)}
                    className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-xs font-bold transition-all shadow-md active:scale-95 shrink-0"
                  >
                    {copiedId === account.id ? (
                      <>
                        <Check size={13} />
                        <span>Copied</span>
                      </>
                    ) : (
                      <>
                        <Copy size={13} />
                        <span>Copy</span>
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Account Holder Name */}
              <div className="pt-2 border-t border-white/10 flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block">
                    Account Name
                  </span>
                  <p className="text-sm font-bold text-slate-200 truncate">
                    {account.account_name}
                  </p>
                </div>
                <CreditCard className="text-white/20 shrink-0" size={22} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* View Account Details Modal */}
      {viewAccount && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-300">
          <div className="bg-white rounded-[2.5rem] w-full max-w-md p-8 shadow-2xl border border-slate-100 relative animate-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="flex justify-between items-center mb-6 pb-4 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center">
                  <Landmark size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-black text-slate-800">
                    Account Details
                  </h3>
                  <p className="text-slate-400 font-bold text-xs">
                    {viewAccount.bank_name}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setViewAccount(null)}
                className="w-10 h-10 rounded-2xl bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition-all"
              >
                <X size={20} />
              </button>
            </div>

            {/* Details Content */}
            <div className="space-y-6">
              <div className="bg-[#F8FAFC] rounded-2xl p-4 border border-slate-100">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">
                  Bank Name
                </span>
                <p className="text-lg font-black text-slate-800">
                  {viewAccount.bank_name}
                </p>
              </div>

              <div className="bg-[#F8FAFC] rounded-2xl p-4 border border-slate-100 flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">
                    Account Number
                  </span>
                  <p className="font-mono text-2xl font-black text-slate-900 tracking-wider">
                    {viewAccount.account_number}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleCopy(viewAccount.account_number, viewAccount.id)}
                  className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold transition-all shadow-md"
                >
                  {copiedId === viewAccount.id ? (
                    <>
                      <Check size={14} />
                      <span>Copied</span>
                    </>
                  ) : (
                    <>
                      <Copy size={14} />
                      <span>Copy</span>
                    </>
                  )}
                </button>
              </div>

              <div className="bg-[#F8FAFC] rounded-2xl p-4 border border-slate-100">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">
                  Account Holder Name
                </span>
                <p className="text-base font-bold text-slate-800">
                  {viewAccount.account_name}
                </p>
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex items-center justify-between gap-3 pt-6 mt-6 border-t border-slate-100">
              <button
                type="button"
                onClick={() => {
                  const target = viewAccount;
                  setViewAccount(null);
                  openEditModal(target);
                }}
                className="flex items-center gap-2 px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-2xl font-black text-xs uppercase tracking-widest transition-all"
              >
                <Edit3 size={16} /> Edit Account
              </button>
              <button
                type="button"
                onClick={() => setViewAccount(null)}
                className="bg-[#0F172A] hover:bg-slate-800 text-white px-8 py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all shadow-lg"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal for Add / Edit */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-300">
          <div className="bg-white rounded-[2.5rem] w-full max-w-lg p-8 shadow-2xl border border-slate-100 relative animate-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="flex justify-between items-center mb-6 pb-4 border-b border-slate-100">
              <div>
                <h3 className="text-2xl font-black text-slate-800">
                  {editingId ? "Edit Bank Account" : "Add Bank Account"}
                </h3>
                <p className="text-slate-400 font-bold text-xs">
                  {editingId
                    ? "Update school bank account credentials."
                    : "Enter details for the new school bank account."}
                </p>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="w-10 h-10 rounded-2xl bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition-all"
              >
                <X size={20} />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Account Name */}
              <div>
                <label className={labelStyle}>Account Holder Name</label>
                <div className={inputContainer}>
                  <Building2 className="text-slate-300" size={18} />
                  <input
                    type="text"
                    value={formData.account_name}
                    onChange={(e) =>
                      setFormData({ ...formData, account_name: e.target.value })
                    }
                    placeholder="e.g. Pendulum Digital School"
                    className={inputStyle}
                    required
                  />
                </div>
              </div>

              {/* Account Number */}
              <div>
                <label className={labelStyle}>Account Number</label>
                <div className={inputContainer}>
                  <CreditCard className="text-slate-300" size={18} />
                  <input
                    type="text"
                    value={formData.account_number}
                    onChange={(e) =>
                      setFormData({ ...formData, account_number: e.target.value })
                    }
                    placeholder="e.g. 9876543210"
                    className={inputStyle}
                    required
                  />
                </div>
              </div>

              {/* Bank Name */}
              <div>
                <label className={labelStyle}>Bank Name</label>
                <div className={inputContainer}>
                  <Landmark className="text-slate-300" size={18} />
                  <input
                    type="text"
                    value={formData.bank_name}
                    onChange={(e) =>
                      setFormData({ ...formData, bank_name: e.target.value })
                    }
                    placeholder="e.g. Access Bank, GTBank, Opay..."
                    className={inputStyle}
                    required
                  />
                </div>
              </div>

              {/* Modal Buttons */}
              <div className="flex items-center justify-end gap-4 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-6 py-3.5 rounded-2xl font-black text-xs uppercase tracking-widest text-slate-400 hover:text-slate-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="bg-[#0F172A] hover:bg-slate-800 text-white px-8 py-3.5 rounded-2xl font-black text-xs uppercase tracking-widest transition-all shadow-xl flex items-center gap-2 disabled:opacity-50"
                >
                  <Save size={16} />
                  {submitting ? "Saving..." : editingId ? "Update Account" : "Create Account"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankAccountSetting;
